import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eye, FileText, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import api, { uploadRequestConfig, uploadErrorMessage } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import PolicyViewer from '../common/PolicyViewer';

const MAX_FILE_MB = 50;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatSize = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(2)} MB` : '--');

const PolicyManagement = () => {
  const [categories, setCategories] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderFilter, setFolderFilter] = useState('');
  const [viewingPolicy, setViewingPolicy] = useState(null);

  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [categoriesResponse, policiesResponse] = await Promise.all([
        api.get('/policies/categories'),
        api.get('/policies'),
      ]);
      setCategories(categoriesResponse.data.categories || []);
      setPolicies(policiesResponse.data.policies || []);
    } catch (error) {
      toast.error('Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      event.target.value = '';
      return;
    }
    if (selected.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File size exceeds ${MAX_FILE_MB}MB limit`);
      event.target.value = '';
      return;
    }
    setFile(selected);
    if (!title) setTitle(selected.name.replace(/\.pdf$/i, ''));
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!category || !title.trim() || !file) {
      toast.error('Please choose a folder, enter a title and select a PDF');
      return;
    }

    const formData = new FormData();
    formData.append('category', category);
    formData.append('title', title.trim());
    formData.append('file', file);

    setUploading(true);
    setProgress(0);
    try {
      await api.post('/admin/policies', formData, uploadRequestConfig(setProgress));
      toast.success('Policy uploaded successfully');
      setTitle('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(uploadErrorMessage(error, 'Failed to upload policy'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (policy) => {
    if (!window.confirm(`Delete "${policy.title}"? Employees will no longer be able to view it.`)) return;
    try {
      await api.delete(`/admin/policies/${policy._id}`);
      toast.success('Policy deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete policy');
    }
  };

  const labelFor = (value) => categories.find((c) => c.value === value)?.label || value;
  const visiblePolicies = folderFilter ? policies.filter((p) => p.category === folderFilter) : policies;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Company Policies</h1>
        <p className="text-gray-600">Upload approved policies. Employees can only view them, not download or print.</p>
      </div>

      <form onSubmit={handleUpload} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Upload Policy</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Select folder</option>
              {categories.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Policy Title</label>
            <input
              type="text"
              value={title}
              maxLength={150}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Leave Policy"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PDF File (max {MAX_FILE_MB}MB)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          {uploading && <span className="text-sm text-gray-500">Uploading… {progress}%</span>}
          <button
            type="submit"
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            Upload
          </button>
        </div>
      </form>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Uploaded Policies ({visiblePolicies.length})</h2>
          <select
            value={folderFilter}
            onChange={(event) => setFolderFilter(event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All folders</option>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label} ({c.count})</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="p-12"><LoadingSpinner size="lg" className="mx-auto" /></div>
        ) : visiblePolicies.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No policies uploaded yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Folder</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visiblePolicies.map((policy) => (
                  <tr key={policy._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{policy.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{labelFor(policy.category)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatSize(policy.size)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(policy.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={() => setViewingPolicy(policy)}
                        className="mr-2 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(policy)}
                        className="rounded-md p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewingPolicy && <PolicyViewer policy={viewingPolicy} onClose={() => setViewingPolicy(null)} />}
    </div>
  );
};

export default PolicyManagement;
